"use client";

import React from "react";
import { Grid, Grid3X3, LayoutGrid, List, Table } from "lucide-react";
import { Select, SelectProps } from "modules/common/components/ui/Select";
import { ICON_SIZE } from "modules/common/components/navigation/Menu";
import { cn } from "utils/utils";
import { CollectionViews } from "modules/collections/details/model/collection";
import { TradeItemViews } from "modules/tradeLists/model/tradeList"; // You'll need to ensure this path is correct
import { clientLogger } from "modules/common/logger/client";

// Base interface for view options
export interface ViewOption {
  name: string;
  icon?: (highlighted: boolean) => React.ReactNode;
}

// Known view types for the application
export type ViewType = "collection" | "tradeItem" | "custom";

interface ViewSelectProps<T extends ViewOption> extends Pick<SelectProps, "size"> {
  // Make options optional since we can derive them from viewType
  options?: readonly T[];
  activeView: T;
  onChange: (value: string | number) => void;
  viewType?: ViewType;
  className?: string;
  defaultIcon?: (highlighted: boolean) => React.ReactNode;
}

// Icon mapping helper function
function getIconForViewName(
  viewName: string,
  highlighted: boolean
): React.ReactNode {
  const highlightClass = highlighted ? "text-base-100" : "text-base-content/70";

  switch (viewName) {
    case "Visual":
      return <LayoutGrid size={ICON_SIZE} className={highlightClass} />;
    case "Table":
      return <Table size={ICON_SIZE} className={highlightClass} />;
    case "List":
      return <List size={ICON_SIZE} className={highlightClass} />;
    case "Grid":
      return <Grid size={ICON_SIZE} className={highlightClass} />;
    case "Compact":
      return <Grid3X3 size={ICON_SIZE} className={highlightClass} />;
    default:
      return <Table size={ICON_SIZE} className={highlightClass} />;
  }
}

// Get options array based on viewType
function getOptionsForViewType(viewType: ViewType): readonly ViewOption[] {
  switch (viewType) {
    case "collection":
      return CollectionViews;
    case "tradeItem":
      return TradeItemViews;
    default:
      return [];
  }
}

export function ViewSelect<T extends ViewOption>({
  options,
  activeView,
  onChange,
  viewType = "custom",
  className,
  defaultIcon,
  size,
}: ViewSelectProps<T>) {
  // Use provided options or get default options based on viewType
  const viewOptions =
    options ||
    (viewType !== "custom"
      ? (getOptionsForViewType(viewType) as unknown as readonly T[])
      : []);

  // Find current index
  const currentIndex = viewOptions.findIndex(
    (view) => view.name === activeView.name
  );

  // If no options provided and viewType is custom, warn (clientLogger
  // already drops in production, so the NODE_ENV gate is redundant).
  if (!options && viewType === "custom") {
    clientLogger.warn(
      "viewSelect",
      "viewType='custom' requires options to be provided"
    );
  }

  return (
    <Select
      name="view"
      options={viewOptions.map((view, idx) => ({
        value: idx,
        label: view.name,
        // Note: Removed icon for native select compatibility
      }))}
      value={currentIndex}
      onChange={onChange}
      label="View"
      size={size}
      className={cn(className)}
    />
  );
}
